import { useCallback, useEffect, useRef, useState } from 'react';
import { Terminal, type IDisposable, type ITheme } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { api } from '~/lib/api-client';
import { useTheme } from '~/lib/stores/theme-store';
import { createInputQueue } from './input-queue';
import { resolveCssFontStack, resolveTerminalBackground } from './resolve-css-color';
import {
  FOLLOW_APP_FONT_LOAD_SPEC,
  FOLLOW_APP_MIN_CONTRAST,
  buildTerminalTheme,
  followAppFontOptions,
  createSchemeProtocol,
  installSchemeProtocol,
  whenWebFontReady,
  type SchemeProtocol,
} from './terminal-appearance';
import { installOsc52Clipboard } from './osc52-clipboard';
import { installStitchedLinks } from './stitched-links';
import { reattachRepaintSizes } from './reattach-repaint';
import { keepAliveSize, startKeepAlive, type TerminalSize } from './keepalive';
import {
  shouldEndSessionOnPageHide,
  shouldEndSessionOnTeardown,
  shouldRepaintOnOpen,
  openTracked,
  trackPendingEnd,
  waitForPendingEnds,
  type EndSessionOnTeardown,
} from './pty-teardown';

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 10_000;
const RESIZE_DEBOUNCE_MS = 150;
/** Give up reconnecting after this many consecutive failed attempts (FRONT-956 review). */
const MAX_RECONNECT_ATTEMPTS = 5;

/** Matches the app's `--card`/`--foreground` design tokens (FRONT-956). Literal hex on purpose: xterm's canvas cannot read CSS variables. */
export const TERMINAL_THEME: ITheme = {
  background: '#18181b',
  foreground: '#e4e4e7',
  cursor: '#e4e4e7',
  selectionBackground: 'rgba(228, 228, 231, 0.25)',
};

export const TERMINAL_FONT_FAMILY = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
export const TERMINAL_FONT_SIZE = 12;

/**
 * Decode a base64 SSE `event: data` payload into the UTF-8 text xterm
 * should write. The backend re-encodes already-decoded PTY output as
 * base64 (see `src/apps/terminal-routes.ts`'s `/stream` handler) so the
 * wire payload is opaque to whatever the shell prints (including raw
 * control bytes that would otherwise need SSE-frame escaping).
 */
export function decodeBase64ToText(b64: string): string {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder('utf-8').decode(bytes);
}

/**
 * Capped exponential backoff, doubling from `RECONNECT_BASE_MS` up to
 * `RECONNECT_MAX_MS` — same shape as `use-coding-session.ts`'s reconnect.
 */
export function nextBackoffMs(current: number): number {
  return Math.min(current * 2, RECONNECT_MAX_MS);
}

/**
 * Whether the SSE stream should attempt another reconnect after `attempts`
 * consecutive failures (FRONT-956 review: reconnect was previously unbounded).
 */
export function shouldReconnect(attempts: number): boolean {
  return attempts < MAX_RECONNECT_ATTEMPTS;
}

export { RECONNECT_BASE_MS, RECONNECT_MAX_MS, RESIZE_DEBOUNCE_MS, MAX_RECONNECT_ATTEMPTS };

/**
 * Sandbox warm-up poll (FRONT-968). Opening a terminal cold used to block the
 * request while the per-user sandbox provisioned (~90s) and 502 at the gateway.
 * The client now POSTs `…/terminal/prewarm` on a steady interval until it
 * reports `ready`, then opens (a fast, warm spawn). Bounded so a sandbox that
 * never comes up surfaces an error instead of polling forever — the 2-minute
 * ceiling sits just above the manager's 90s provision timeout.
 */
const PROVISION_POLL_MS = 2_000;
const PROVISION_MAX_ATTEMPTS = 60;
export { PROVISION_POLL_MS, PROVISION_MAX_ATTEMPTS };

/** Whether to poll prewarm again after `attempts` not-ready replies. */
export function shouldKeepPolling(attempts: number): boolean {
  return attempts < PROVISION_MAX_ATTEMPTS;
}

/** Resolve after `ms`. The prewarm poll re-checks `cancelled` on wake. */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { window.setTimeout(resolve, ms); });
}

/** Prewarm reports the sandbox's readiness for opening a PTY. */
export type PrewarmResponse = { state: 'ready' | 'provisioning' | 'error'; detail?: string };

export type TerminalConnectionState = 'connecting' | 'open' | 'ended' | 'error';

/**
 * Where a terminal target's PTY lives. The hook never builds a URL itself, so
 * the app sandbox and the person's own machine share one implementation
 * (FRONT-2523). `prewarm` is optional: only the app sandbox has a cold start.
 */
export interface PtyEndpoints {
  open: string;
  prewarm?: string;
  stream: (id: string) => string;
  input: (id: string) => string;
  resize: (id: string) => string;
  kill: (id: string) => string;
}

/** The `error` SSE frame's code when the server no longer has the session. */
export const NO_SESSION_CODE = 'no-session';

/**
 * Whether an SSE `error` event is the server telling us the session is gone.
 * A native connection error has no `data`, so it is never terminal here.
 */
export function isSessionGoneEvent(raw: unknown): boolean {
  const data = (raw as { data?: unknown } | null)?.data;
  if (typeof data !== 'string') return false;
  try {
    return (JSON.parse(data) as { code?: unknown }).code === NO_SESSION_CODE;
  } catch {
    return false;
  }
}

/**
 * A hidden container (a background tab) measures 0x0; fitting to it would
 * resize the remote PTY to a couple of columns and wreck the shell's layout.
 */
export function isFittable(el: { clientWidth: number; clientHeight: number } | null): boolean {
  return !!el && el.clientWidth > 0 && el.clientHeight > 0;
}

/**
 * The size to send right after the open request answers, or null when the PTY
 * already has it. A refit while the open is in flight has no session id to
 * resize yet, so without this the PTY would keep the size the open carried
 * while the terminal shows another — and the keepalive's re-sent "current
 * size" would quietly resize it 10 minutes later.
 */
export function sizeToSyncAfterOpen(sent: TerminalSize, current: TerminalSize): TerminalSize | null {
  if (current.cols === sent.cols && current.rows === sent.rows) return null;
  return keepAliveSize(current);
}

/** Shown once the stream's automatic retries are used up. */
export const RECONNECT_EXHAUSTED_MESSAGE = 'Terminal disconnected. Press Reconnect to start a new session.';

/**
 * What a dropped stream does next, after `attempts` consecutive failures:
 * retry quietly with backoff (`reconnecting` stays true, the session and its
 * keepalive stay up), or give up — say so, stop reconnecting, and stop the
 * keepalive so nothing keeps pinging a session we have stopped talking to.
 */
export function streamErrorOutcome(
  attempts: number,
): { reconnecting: true; errorMessage: null; stopKeepAlive: false } | { reconnecting: false; errorMessage: string; stopKeepAlive: true } {
  return shouldReconnect(attempts)
    ? { reconnecting: true, errorMessage: null, stopKeepAlive: false }
    : { reconnecting: false, errorMessage: RECONNECT_EXHAUSTED_MESSAGE, stopKeepAlive: true };
}

/** The 201 body of the open request. `startedWith` is Pro Builder's own field and is opaque to this hook. */
export interface PtyOpenResponse {
  id: string;
  pid?: number;
  attached?: boolean;
  startedWith?: unknown;
}

interface UsePtyTerminalOptions {
  container: HTMLDivElement | null;
  endpoints: PtyEndpoints;
  /**
   * Let programs in the terminal copy to the clipboard with OSC 52 (writes
   * only, and only right after the person's key press or mouse action — see
   * `osc52-clipboard.ts`). Default false: only the Pro Builder
   * terminal opts in (FRONT-3003), the app and machine terminals register
   * no handler and behave exactly as before.
   */
  clipboard?: boolean;
  /**
   * Called when the browser refuses a clipboard write the terminal honoured
   * (`clipboard: true` only), so the caller can tell the person (FRONT-3172).
   */
  onCopyRefused?: () => void;
  /**
   * Make http(s) URLs clickable, stitching a URL a program hard-wrapped over
   * several rows into one link (`stitched-links.ts`, FRONT-3172). Default
   * false: only the Pro Builder terminal opts in.
   */
  links?: boolean;
  /**
   * `'fixed'` (default): the long-standing dark `TERMINAL_THEME`, untouched.
   * `'follow-app'`: the terminal follows Eddy's light/dark theme live, paints
   * the `--card` panel colour, and answers the colour-scheme notification
   * protocol so a program such as Claude Code can follow too
   * (`terminal-appearance.ts`). Only Pro Builder opts in.
   */
  appearance?: 'fixed' | 'follow-app';
  /**
   * Default false (app and machine terminals unchanged). When true, an open
   * session re-sends its CURRENT size every 10 minutes so the sandbox daemon's
   * 30-minute idle reaper never fires while the window is hidden (`keepalive.ts`).
   */
  keepAlive?: boolean;
  /**
   * Default false (app and machine terminals unchanged). The PTY's output is
   * not replayed to a stream that attaches late (nothing in the daemon or
   * backend buffers it), so a stream that opens after a failed or closed
   * attempt for the same session leaves the screen blank or stale. When true,
   * that re-attach nudges the PTY to (cols, rows-1) and back to the real size,
   * so SIGWINCH makes a full-screen program such as Claude Code repaint
   * (`reattach-repaint.ts`). The first clean stream of a session never does.
   */
  redrawOnReattach?: boolean;
  /**
   * Called after EVERY successful open of a new PTY: the first one, a
   * Reconnect, and the restart after a back/forward-cache restore (all three
   * run the same open below). A stream that merely re-attaches to the same
   * session after a network drop does not call it — no new shell was started.
   * It receives the open response: Pro Builder reads `startedWith` from it,
   * what the shell (spawned now or attached to) was spawned with.
   */
  onSessionOpened?: (opened: PtyOpenResponse) => void;
  /**
   * Default true (every terminal but Pro Builder's): the shell is ended when
   * the component unmounts and when the page closes. When false (FRONT-3040,
   * Pro Builder: the session outlives the tab) teardown only DETACHES — page
   * close and unmount send no DELETE, and a back/forward-cache restore opens
   * a stream again through the normal open path, which the server answers by
   * attaching to the live shell. A function is read at the moment of
   * teardown, so the caller can still end the shell when a restart is
   * replacing it (`pty-teardown.ts`).
   */
  endSessionOnTeardown?: EndSessionOnTeardown;
}

interface UsePtyTerminalResult {
  state: TerminalConnectionState;
  errorMessage: string | null;
  /**
   * True while a dropped stream is being retried with backoff and the retries
   * are not used up: the session is still alive server-side, so this is not an
   * ended terminal and nothing should offer to replace it.
   */
  reconnecting: boolean;
  cols: number;
  rows: number;
  reconnect: () => void;
}

/**
 * Target-agnostic PTY-over-SSE hook (the app sandbox terminal, the person's own
 * machine terminal and the Pro Builder terminal all run on it). Owns the xterm `Terminal` instance, the PTY session lifecycle
 * (spawn → SSE stream → input/resize → teardown), and reconnect-with-backoff
 * — mirrors `useCodingSession`'s SSE-down + POST-up shape for the app
 * terminal's single always-on session (no replay/tape semantics needed here,
 * PTY output isn't persisted).
 */
export function usePtyTerminal({ container, endpoints, clipboard = false, onCopyRefused, links = false, appearance = 'fixed', keepAlive = false, redrawOnReattach = false, onSessionOpened, endSessionOnTeardown }: UsePtyTerminalOptions): UsePtyTerminalResult {
  const endpointsRef = useRef(endpoints);
  endpointsRef.current = endpoints;
  const onSessionOpenedRef = useRef(onSessionOpened);
  onSessionOpenedRef.current = onSessionOpened;
  const endSessionOnTeardownRef = useRef(endSessionOnTeardown);
  endSessionOnTeardownRef.current = endSessionOnTeardown;
  const clipboardRef = useRef(clipboard);
  clipboardRef.current = clipboard;
  const onCopyRefusedRef = useRef(onCopyRefused);
  onCopyRefusedRef.current = onCopyRefused;
  const linksRef = useRef(links);
  linksRef.current = links;
  const keepAliveRef = useRef(keepAlive);
  keepAliveRef.current = keepAlive;
  const redrawOnReattachRef = useRef(redrawOnReattach);
  redrawOnReattachRef.current = redrawOnReattach;
  // True once a stream for the current session failed/closed; consumed by the next open.
  const streamFailedRef = useRef(false);
  // Set by the setup effect: sends the repaint nudge for a re-attached stream.
  const repaintRef = useRef<(enabled: boolean) => void>(() => {});
  const appearanceRef = useRef(appearance);
  appearanceRef.current = appearance;
  // The resolved app theme (the `system` preference already followed the OS).
  // Read for every terminal so the hook count never varies; only 'follow-app' uses it.
  const { theme: appTheme } = useTheme();
  const appThemeRef = useRef(appTheme);
  appThemeRef.current = appTheme;
  const schemeRef = useRef<SchemeProtocol | null>(null);
  // Endpoints identify the target; a new target means a new session.
  const targetKey = endpoints.open;
  const [state, setState] = useState<TerminalConnectionState>('connecting');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [cols, setCols] = useState(0);
  const [rows, setRows] = useState(0);
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const reconnect = useCallback(() => setReconnectNonce((n) => n + 1), []);

  const termRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const esRef = useRef<EventSource | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const backoffRef = useRef(RECONNECT_BASE_MS);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const resizeTimerRef = useRef<number | null>(null);
  const aliveRef = useRef(true);
  // Set by the setup effect; lets the stream handlers (declared outside it) stop its keepalive.
  const endKeepAliveRef = useRef<() => void>(() => {});
  const sessionGoneRef = useRef(false);

  const closeStream = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    esRef.current?.close();
    esRef.current = null;
  }, []);

  const openStream = useCallback(
    (id: string) => {
      closeStream();
      const es = new EventSource(endpointsRef.current.stream(id), { withCredentials: true });
      esRef.current = es;

      es.addEventListener('data', (raw) => {
        const msg = raw as MessageEvent<string>;
        backoffRef.current = RECONNECT_BASE_MS;
        reconnectAttemptsRef.current = 0;
        setReconnecting(false);
        setState('open');
        try {
          termRef.current?.write(decodeBase64ToText(msg.data));
        } catch {
          /* malformed frame — skip rather than crash the terminal */
        }
      });

      es.addEventListener('close', () => {
        termRef.current?.write('\r\n\x1b[2m[session ended]\x1b[0m\r\n');
        setReconnecting(false);
        setState('ended');
        closeStream();
        endKeepAliveRef.current();
        schemeRef.current?.reset();
      });

      es.addEventListener('error', (raw) => {
        if (isSessionGoneEvent(raw)) {
          // The server no longer has this shell: retrying the stream can never
          // succeed, so end the session. Reconnect opens a fresh one.
          sessionGoneRef.current = true;
          endKeepAliveRef.current();
          schemeRef.current?.reset();
          termRef.current?.write('\r\n\x1b[2m[session ended]\x1b[0m\r\n');
          setReconnecting(false);
          setState('ended');
          closeStream();
          return;
        }
        // Not terminal by itself: `onerror` below decides between a quiet
        // retry (`reconnecting`) and giving up with a message.
        setState('error');
      });

      // An idle shell can go a long time without a `data` frame, so don't
      // rely on that alone to flip the state back to open after a reconnect —
      // `open` fires as soon as the connection is (re)established.
      es.onopen = () => {
        if (streamFailedRef.current) {
          streamFailedRef.current = false;
          repaintRef.current(redrawOnReattachRef.current);
        }
        backoffRef.current = RECONNECT_BASE_MS;
        reconnectAttemptsRef.current = 0;
        setReconnecting(false);
        setState((prev) => (prev === 'connecting' || prev === 'error' ? 'open' : prev));
      };

      es.onerror = () => {
        streamFailedRef.current = true;
        es.close();
        if (esRef.current === es) esRef.current = null;
        if (sessionGoneRef.current) return;
        if (!aliveRef.current || sessionIdRef.current !== id) return;

        reconnectAttemptsRef.current += 1;
        const outcome = streamErrorOutcome(reconnectAttemptsRef.current);
        setReconnecting(outcome.reconnecting);
        if (outcome.stopKeepAlive) {
          // Retries used up: stop pinging a session we no longer talk to.
          endKeepAliveRef.current();
          setState('error');
          setErrorMessage(outcome.errorMessage);
          return;
        }

        const delay = backoffRef.current;
        backoffRef.current = nextBackoffMs(backoffRef.current);
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null;
          if (aliveRef.current && sessionIdRef.current === id) openStream(id);
        }, delay);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeStream],
  );

  useEffect(() => {
    aliveRef.current = true;
    if (!container) return;

    setState('connecting');
    setErrorMessage(null);
    setReconnecting(false);
    reconnectAttemptsRef.current = 0;
    backoffRef.current = RECONNECT_BASE_MS;
    sessionGoneRef.current = false;
    const ep = endpointsRef.current;

    const followApp = appearanceRef.current === 'follow-app';
    // A following terminal wears Eddy's own mono face (read live from `--font-mono`).
    // xterm measures its cell grid when it opens, so until the web font is in
    // the terminal opens on the stack WITHOUT it and is re-measured below.
    const appFont = followApp ? resolveCssFontStack(container) : null;
    const fontReady = typeof document === 'undefined' || !document.fonts || document.fonts.check(FOLLOW_APP_FONT_LOAD_SPEC);
    const term = new Terminal({
      ...(appFont
        ? followAppFontOptions(appFont, fontReady)
        : { fontFamily: TERMINAL_FONT_FAMILY, fontSize: TERMINAL_FONT_SIZE }),
      theme: followApp
        ? buildTerminalTheme(appThemeRef.current, resolveTerminalBackground(container, appThemeRef.current))
        : TERMINAL_THEME,
      ...(followApp ? { minimumContrastRatio: FOLLOW_APP_MIN_CONTRAST } : {}),
      cursorBlink: true,
      convertEol: true,
    });
    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(container);
    fitAddon.fit();
    // Holds listeners on the container, its document and its window too, so teardown disposes it explicitly.
    const osc52 = installOsc52Clipboard(
      term,
      clipboardRef.current,
      (text) => {
        // A browser with no clipboard API counts as a refusal, not a silent success (FRONT-3172).
        if (!navigator.clipboard) throw new Error('clipboard unavailable');
        return navigator.clipboard.writeText(text);
      },
      Date.now,
      {
        container,
        ownerDocument: container.ownerDocument,
        ownerWindow: container.ownerDocument.defaultView,
      },
      undefined,
      { onRefused: () => onCopyRefusedRef.current?.() },
    );
    // Disposed with the terminal itself (`term.dispose()` on teardown).
    if (linksRef.current) installStitchedLinks(term);
    termRef.current = term;
    fitAddonRef.current = fitAddon;
    setCols(term.cols);
    setRows(term.rows);

    // Everything onData emits — typed keys AND xterm's own replies to terminal
    // queries (device attributes, cursor position) — goes through ONE ordered
    // queue, so the shell sees bytes in the order xterm produced them.
    // The session this effect run opened — pinned here rather than read from the
    // shared ref, so typing still queued when Reconnect starts a new run is
    // dropped instead of landing in the NEW shell.
    let runSessionId: string | null = null;
    const inputQueue = createInputQueue(
      (data) => {
        const id = runSessionId;
        return id ? api.post(ep.input(id), { data }) : Promise.resolve();
      },
      (err) => {
        console.debug('terminal input failed', err);
      },
    );
    const onData = term.onData((data) => {
      if (!sessionIdRef.current) return;
      inputQueue.push(data);
    });

    // Colour-scheme notification (mode 2031 / DSR 996): its replies take the
    // same ordered input queue as xterm's own replies above.
    let schemeHooks: IDisposable | null = null;
    if (followApp) {
      const scheme = createSchemeProtocol((bytes) => {
        if (sessionIdRef.current) inputQueue.push(bytes);
      }, appThemeRef.current);
      schemeHooks = installSchemeProtocol(term, scheme);
      schemeRef.current = scheme;
    }

    let cancelled = false;
    // Last size the PTY was told. A hidden window measures 0x0 and never refits,
    // so the keepalive reads this rather than the DOM.
    let lastSize: TerminalSize | null = { cols: term.cols, rows: term.rows };
    let stopKeepAlive: (() => void) | null = null;
    const endKeepAlive = () => {
      stopKeepAlive?.();
      stopKeepAlive = null;
    };
    endKeepAliveRef.current = endKeepAlive;

    async function startSession() {
      // Prewarm the sandbox first (FRONT-968), polling until it's ready, so the
      // open() below only runs against a warm pod and never eats the cold-start
      // wait that used to 502 the terminal at the gateway.
      let attempts = 0;
      while (!cancelled && ep.prewarm) {
        let warm: PrewarmResponse;
        try {
          warm = await api.post<PrewarmResponse>(ep.prewarm, {});
        } catch (err) {
          if (cancelled) return;
          setState('error');
          setErrorMessage((err as Error).message || "Terminal isn't available right now.");
          return;
        }
        if (cancelled) return;
        if (warm.state === 'ready') break;
        if (warm.state === 'error') {
          setState('error');
          setErrorMessage(warm.detail || 'The sandbox failed to start.');
          return;
        }
        attempts += 1;
        if (!shouldKeepPolling(attempts)) {
          setState('error');
          setErrorMessage('The sandbox is taking too long to start. Press Reconnect to try again.');
          return;
        }
        setState('connecting');
        // ponytail: plain sleep; the loop re-checks `cancelled` on wake, so an
        // unmount mid-wait just exits without another POST — no timer to track.
        await sleep(PROVISION_POLL_MS);
      }
      if (cancelled) return;

      // FRONT-3040: a restart's old shell must be gone before this open can
      // attach to it (only a terminal that passes endSessionOnTeardown waits).
      if (endSessionOnTeardownRef.current !== undefined) await waitForPendingEnds();
      if (cancelled) return;

      // Sandbox is warm — open the PTY (fast, returns 201 { id, pid }).
      try {
        const sent: TerminalSize = { cols: term.cols, rows: term.rows };
        // FRONT-3040: the open is recorded as pending from the moment it is
        // sent; a cancelled one ends the shell it created (a detaching
        // terminal must not end a shell it merely attached to) and the
        // restart's replacement waits for that DELETE like any other end.
        const opened = await openTracked(
          () => api.post<PtyOpenResponse>(ep.open, sent),
          (o) => api.delete(ep.kill(o.id)),
          () => cancelled,
          endSessionOnTeardownRef.current,
        );
        const { id, attached } = opened;
        if (cancelled) return;
        sessionIdRef.current = id;
        runSessionId = id;
        streamFailedRef.current = false;
        // FRONT-3040: the server re-used the person's live shell, whose screen
        // this fresh terminal has never seen. Marking the stream as following
        // a failed attempt makes its first `open` run the same repaint nudge
        // the reconnect path runs.
        if (shouldRepaintOnOpen(attached, redrawOnReattachRef.current)) streamFailedRef.current = true;
        // A refit while the open was in flight had no id to resize: catch the PTY up.
        const drift = sizeToSyncAfterOpen(sent, { cols: term.cols, rows: term.rows });
        if (drift) {
          lastSize = drift;
          void api.post(ep.resize(id), drift).catch((err) => {
            console.debug('terminal resize failed', err);
          });
        }
        if (keepAliveRef.current) {
          stopKeepAlive = startKeepAlive(
            () => lastSize,
            (size) => {
              void api.post(ep.resize(id), size).catch((err) => {
                console.debug('terminal keepalive failed', err);
              });
            },
          );
        }
        repaintRef.current = (enabled) => {
          const sizes = reattachRepaintSizes({ enabled, followsFailedAttempt: true, lastSize });
          if (!sizes) return;
          // Sequential, so the PTY sees the nudge before the real size; the real
          // size is read at send time so a refit in between is not undone.
          void api
            .post(ep.resize(id), sizes.nudge)
            .catch((err) => console.debug('terminal repaint nudge failed', err))
            .then(() => {
              const restore =
                reattachRepaintSizes({ enabled, followsFailedAttempt: true, lastSize })?.restore ?? sizes.restore;
              return api.post(ep.resize(id), restore);
            })
            .catch((err) => console.debug('terminal repaint restore failed', err));
        };
        openStream(id);
        // The one place a new PTY is reported: Reconnect and the bfcache
        // restart both re-run this effect, so they arrive here too.
        onSessionOpenedRef.current?.(opened);
      } catch (err) {
        if (cancelled) return;
        setState('error');
        setErrorMessage((err as Error).message || "Terminal isn't available right now.");
      }
    }

    void startSession();

    const refit = () => {
      const addon = fitAddonRef.current;
      const t = termRef.current;
      const id = sessionIdRef.current;
      if (!addon || !t || !isFittable(container)) return;
      addon.fit();
      setCols(t.cols);
      setRows(t.rows);
      lastSize = { cols: t.cols, rows: t.rows };
      if (id) {
        void api
          .post(ep.resize(id), { cols: t.cols, rows: t.rows })
          .catch((err) => {
            console.debug('terminal resize failed', err);
          });
      }
    };
    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimerRef.current !== null) window.clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = window.setTimeout(() => {
        resizeTimerRef.current = null;
        refit();
      }, RESIZE_DEBOUNCE_MS);
    });
    resizeObserver.observe(container);

    // Web font arrives after open: swap it in (xterm re-measures on a fontFamily
    // change) and refit — but only once the face is really usable, however long
    // that takes, because naming a face before it loads leaves xterm on the
    // fallback's cell metrics. Never recreates the terminal or waits on the PTY;
    // a font that never loads just leaves the terminal on the fallback stack.
    const stopFontWatch =
      appFont && !fontReady
        ? whenWebFontReady(document.fonts, FOLLOW_APP_FONT_LOAD_SPEC, () => {
            if (cancelled || termRef.current !== term) return;
            term.options.fontFamily = appFont;
            refit();
          })
        : null;

    // Two ways to end the shell, sent at most once between them. A normal close
    // goes through the api client, which renews an expired login and retries —
    // a bare request after an hour idle would be refused and leave the shell
    // running. Reload / tab close can't wait for that, so it uses a keepalive
    // request that outlives the page (unmount cleanup does not run there).
    // A page closed while the open request is still in flight has no id yet and
    // sends nothing; the daemon's idle reaper is the backstop for that window.
    let killSent = false;
    // FRONT-3040: a detaching terminal's page-hide sends nothing; remember it
    // so a back/forward-cache restore re-opens (attaches) like a kill would.
    let detachedOnPageHide = false;
    const killOnce = (id: string, pageClosing: boolean) => {
      if (killSent) return;
      killSent = true;
      // expected: the PTY may already be gone server-side
      if (pageClosing) {
        void fetch(ep.kill(id), { method: 'DELETE', keepalive: true, credentials: 'same-origin' }).catch(() => {});
      } else {
        const end = api.delete(ep.kill(id)).catch(() => {});
        if (endSessionOnTeardownRef.current !== undefined) trackPendingEnd(end);
      }
    };
    const onPageHide = () => {
      const id = sessionIdRef.current;
      if (!id) return;
      if (shouldEndSessionOnPageHide(endSessionOnTeardownRef.current)) killOnce(id, true);
      else detachedOnPageHide = true;
    };
    // pagehide also fires when the browser keeps the page in its back/forward
    // cache; a shell the page-hide killed is gone by the time it is restored,
    // so start a new one — and one it only detached from is re-attached by the
    // same open (the server answers attached: true).
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted && (killSent || detachedOnPageHide)) setReconnectNonce((n) => n + 1);
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      cancelled = true;
      aliveRef.current = false;
      stopFontWatch?.();
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      resizeObserver.disconnect();
      if (resizeTimerRef.current !== null) {
        window.clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
      endKeepAlive();
      closeStream();
      onData.dispose();
      osc52?.dispose();
      schemeHooks?.dispose();
      schemeRef.current = null;
      term.dispose();
      termRef.current = null;
      fitAddonRef.current = null;
      const id = sessionIdRef.current;
      sessionIdRef.current = null;
      runSessionId = null;
      if (id && shouldEndSessionOnTeardown(endSessionOnTeardownRef.current)) killOnce(id, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, container, closeStream, openStream, reconnectNonce]);

  // Live theme change: re-theme the running terminal in place (never recreate
  // it — that would kill the program inside) and tell the program. Declared
  // after the setup effect so the terminal exists on the first run. The `dark`
  // class is flipped before the store publishes the new theme, so the panel
  // colour read here is already the new one.
  useEffect(() => {
    if (appearance !== 'follow-app') return;
    const term = termRef.current;
    if (!term || !container) return;
    term.options.theme = buildTerminalTheme(appTheme, resolveTerminalBackground(container, appTheme));
    schemeRef.current?.setMode(appTheme);
  }, [appearance, appTheme, container]);

  return { state, errorMessage, reconnecting, cols, rows, reconnect };
}
